import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
// Using SVG icons to avoid TypeScript issues with react-icons
import Overview from './Overview';
import Requests from './Requests';
import Tickets from './Tickets';
import TicketDetail from './TicketDetail';
import CreateTicket from './CreateTicket';
import Profile from './Profile';
import SmartRequestWizard from '../../components/requests/SmartRequestWizard';

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useUserAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  // Update document direction for RTL support
  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  const navigation = [
    { name: t('dashboard.title'), href: '/dashboard', icon: 'home' },
    { name: t('dashboard.requests'), href: '/dashboard/requests', icon: 'folder' },
    { name: t('dashboard.tickets.title'), href: '/dashboard/tickets', icon: 'support' },
    { name: t('dashboard.profile'), href: '/dashboard/profile', icon: 'user' },
  ];

  const isActivePath = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    // Special case for create-request page
    if (location.pathname === '/dashboard/create-request') {
      return t('requestWizard.newCompanyFormationRequest');
    }

    const currentNav = navigation.find(nav => isActivePath(nav.href));
    return currentNav?.name || t('dashboard.title');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 w-full max-w-full overflow-x-hidden">
      <div className="flex">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Enhanced Sidebar */}
        <div className={`fixed inset-y-0 left-0 rtl:right-0 rtl:left-auto z-50 w-72 bg-white/95 backdrop-blur-xl shadow-2xl border-r rtl:border-l rtl:border-r-0 border-slate-200/60 transform transition-transform duration-300 ease-in-out lg:translate-x-0 rtl:lg:translate-x-0 ${
          sidebarOpen
            ? 'translate-x-0 rtl:translate-x-0'
            : '-translate-x-full rtl:translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Logo Section */}
            <div className="p-6 border-b border-slate-200/60">
              <Link to="/" className="flex items-center group hover:opacity-90 transition-opacity">
                <div className="flex items-center justify-center">
                  <img
                    src="/images/logo.png"
                    alt="Patrix Global logo"
                    className="w-10 h-10 object-contain"
                    loading="eager"
                  />
                </div>
                <div className="ml-3 rtl:mr-3 rtl:ml-0">
                  <h2 className="text-lg font-bold text-slate-900 group-hover:text-blue-700 transition-colors">Patrix Global</h2>
                  <p className="text-xs text-slate-500">{t('dashboard.clientDashboard')}</p>
                </div>
              </Link>

              {/* Back to Website Link */}
              <Link
                to="/"
                className="mt-3 flex items-center text-xs text-slate-500 hover:text-blue-600 transition-colors group"
              >
                <svg className="w-3.5 h-3.5 mr-1.5 rtl:ml-1.5 rtl:mr-0 group-hover:-translate-x-0.5 rtl:group-hover:translate-x-0.5 transition-transform"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{t('dashboard.backToWebsite')}</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2">
              {navigation.map((item) => {
                const isActive = isActivePath(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-4 py-3.5 text-sm font-medium rounded-xl transition-all duration-300 ease-out ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25 scale-[1.02]'
                        : 'text-slate-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm hover:scale-[1.01]'
                    }`}
                  >
                    {item.icon === 'home' && (
                      <svg className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-transform duration-300 ${
                        isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-600 group-hover:scale-110'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m3 12 2-2m0 0 7-7 7 7M5 10v10a1 1 0 0 0 1 1h3m10-11 2 2m-2-2v10a1 1 0 0 1-1 1h-3m-6 0a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1m-6 0h6" />
                      </svg>
                    )}
                    {item.icon === 'folder' && (
                      <svg className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-transform duration-300 ${
                        isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-600 group-hover:scale-110'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      </svg>
                    )}
                    {item.icon === 'support' && (
                      <svg className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-transform duration-300 ${
                        isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-600 group-hover:scale-110'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    )}
                    {item.icon === 'user' && (
                      <svg className={`w-5 h-5 mr-3 rtl:ml-3 rtl:mr-0 transition-transform duration-300 ${
                        isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-600 group-hover:scale-110'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                    <span className="font-medium">{item.name}</span>
                    {isActive && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full shadow-sm animate-pulse" />
                    )}
                  </Link>
                );
              })}

              {/* Website Links Section */}
              <div className="pt-4 mt-4 border-t border-slate-200/60">
                <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  {t('dashboard.websiteLinks')}
                </p>

                {/* Browse Services Link */}
                <Link
                  to="/"
                  onClick={() => setSidebarOpen(false)}
                  className="group flex items-center px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-blue-600 rounded-lg transition-all"
                >
                  <svg className="w-4 h-4 mr-3 rtl:ml-3 rtl:mr-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{t('dashboard.browseServices')}</span>
                </Link>

                {/* Contact Link */}
                <Link
                  to="/contact"
                  onClick={() => setSidebarOpen(false)}
                  className="group flex items-center px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-blue-600 rounded-lg transition-all"
                >
                  <svg className="w-4 h-4 mr-3 rtl:ml-3 rtl:mr-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{t('nav.contact')}</span>
                </Link>
              </div>
            </nav>

            {/* User Info & Logout */}
            <div className="p-4 border-t border-slate-200/60">
              <div className="flex items-center px-4 py-3 mb-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-semibold">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="ml-3 rtl:mr-3 rtl:ml-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {user?.name || 'User'}
                  </div>
                  <div className="text-xs text-slate-500">{t('dashboard.premiumMember')}</div>
                </div>
              </div>

              <button
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-300 group"
              >
                <svg className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('dashboard.logout')}
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Main Content */}
        <div className="flex-1 lg:ml-72 rtl:lg:ml-0 rtl:lg:mr-72 w-full max-w-full overflow-x-hidden">
          {/* Enhanced Header */}
          <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
            <div className="px-4 sm:px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {/* Mobile Menu Button */}
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="lg:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors mr-3 rtl:ml-3 rtl:mr-0"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>

                  <h1 className="text-xl lg:text-2xl font-bold text-slate-900 mr-4 rtl:ml-4 rtl:mr-0">
                    {getPageTitle()}
                  </h1>
                  <div className="hidden sm:block">
                    <nav className="flex space-x-4 rtl:space-x-reverse">
                      <span className="text-sm text-slate-500">
                        {new Date().toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </nav>
                  </div>
                </div>

                <div className="flex items-center space-x-2 lg:space-x-4 rtl:space-x-reverse">

                  {/* Language Toggle */}
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-slate-600 hover:text-blue-600 font-medium transition-all duration-200 px-2 lg:px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                    title={i18n.language === 'en' ? t('dashboard.switchToArabic') : t('dashboard.switchToEnglish')}
                  >
                    <span className="hidden sm:inline">{i18n.language === 'en' ? 'العربية' : 'English'}</span>
                    <span className="sm:hidden">{i18n.language === 'en' ? 'ع' : 'EN'}</span>
                  </button>

                  {/* Notifications */}
                  <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                    </svg>
                    <span className="sr-only">View notifications</span>
                  </button>

                  {/* Profile Avatar */}
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="h-full overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
              <Routes>
                <Route path="/" element={<Overview />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/create-request" element={<SmartRequestWizard />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/tickets/create" element={<CreateTicket />} />
                <Route path="/tickets/:ticketId" element={<TicketDetail />} />
                <Route path="/profile" element={<Profile />} />
              </Routes>
            </div>

            {/* Minimal Dashboard Footer */}
            <footer className="mt-auto border-t border-slate-200/60 bg-white/50 backdrop-blur-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
                  {/* Copyright */}
                  <div className="flex items-center gap-1">
                    <span>© {new Date().getFullYear()} Patrix Global.</span>
                    <span className="hidden sm:inline">{t('footer.rights')}</span>
                  </div>

                  {/* Legal & Support Links */}
                  <div className="flex items-center gap-4 flex-wrap justify-center">
                    <Link to="/privacy-policy" className="hover:text-blue-600 transition-colors">
                      {t('footer.privacy')}
                    </Link>
                    <span className="text-slate-300">•</span>
                    <Link to="/terms-of-service" className="hover:text-blue-600 transition-colors">
                      {t('footer.terms')}
                    </Link>
                    <span className="text-slate-300">•</span>
                    <Link to="/contact" className="hover:text-blue-600 transition-colors flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {t('dashboard.needHelp')}
                    </Link>
                  </div>
                </div>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
